import { forwardRef, useCallback, useEffect, useState } from 'react';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Slider } from 'src/core/ui/Slider';

import {
  DEFAULT_RANGE_START,
  MAX_TARGET_ROAS,
  MIN_TARGET_ROAS,
} from './TargetRoasInput.constants';
import * as styles from './TargetRoasInput.css';

type TargetRoasInputProps = {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  rangeStart?: number;
  disabled?: boolean;
  error?: string;
};

export const TargetRoasInput = forwardRef<HTMLDivElement, TargetRoasInputProps>(
  function TargetRoasInput(
    {
      value,
      onChange,
      min = MIN_TARGET_ROAS,
      max = MAX_TARGET_ROAS,
      rangeStart = DEFAULT_RANGE_START,
      disabled = false,
      error,
    },
    ref
  ) {
    // Slider range start extends down if the text input goes below it. The
    // upper bound is a hard cap and never extends past `max`.
    const [dynamicRangeStart, setDynamicRangeStart] = useState(
      Math.min(rangeStart, value)
    );

    const [textValue, setTextValue] = useState(String(value));

    // Sync internal text state when the controlled value prop changes
    // (e.g. when switching bid modes repopulates the ROAS value).
    useEffect(() => {
      setTextValue(String(value));
    }, [value]);

    const handleSliderChange = useCallback(
      (values: number[]) => {
        const newValue = values[0];
        setTextValue(String(newValue));
        onChange(newValue);
      },
      [onChange]
    );

    const handleTextChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setTextValue(e.target.value);
      },
      []
    );

    const handleTextBlur = useCallback(() => {
      const parsed = parseInt(textValue, 10);
      if (isNaN(parsed) || parsed < min) {
        // Reset to current value if invalid
        setTextValue(String(value));
        return;
      }

      const clamped = Math.min(parsed, max);

      // Extend slider range start if needed
      if (clamped < dynamicRangeStart) {
        setDynamicRangeStart(Math.max(min, Math.floor(clamped / 5) * 5));
      }

      setTextValue(String(clamped));
      onChange(clamped);
    }, [textValue, min, max, value, onChange, dynamicRangeStart]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          (e.target as HTMLInputElement).blur();
        }
      },
      []
    );

    // Existing campaigns may carry a value above the cap from before it was
    // enforced; surface it rather than silently rewriting the saved value.
    const displayError =
      error ??
      (value > max ? `Maximum target ROAS is ${max}\u00d7.` : undefined);

    return (
      <div ref={ref} className={styles.container}>
        <div className={styles.inputRow}>
          <div className={styles.sliderContainer}>
            <Slider.Root
              value={[Math.min(value, max)]}
              min={dynamicRangeStart}
              max={max}
              step={1}
              disabled={disabled}
              onValueChange={handleSliderChange}
            />
          </div>
          <div className={styles.textInputContainer}>
            <PosTextField
              type="number"
              inputMode="numeric"
              min={min}
              max={max}
              value={textValue}
              onChange={handleTextChange}
              onBlur={handleTextBlur}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              postfixDisplay={'\u00d7'}
            />
          </div>
        </div>
        {displayError ? (
          <p className={styles.errorText}>{displayError}</p>
        ) : (
          <p className={styles.helperText}>
            For every $1 you spend on ads, you&apos;re targeting ${value} in
            sales.
          </p>
        )}
      </div>
    );
  }
);
